import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { GET_FEATURED_ADS } from '../graphql/queries';
import { getAbsoluteImageUrl } from '../lib/imageUtils';

const PromotionsSidebar: React.FC = () => {
  const { data } = useQuery(GET_FEATURED_ADS, { variables: { limit: 4 } });
  const featuredAds = data?.featuredAds || [];

  const formatPrice = (price: string | null | undefined) => {
    if (!price) return 'Contact';
    const num = parseFloat(price);
    if (isNaN(num)) return price;
    if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toLocaleString();
  };

  const footerLinks = [
    { label: 'Privacy Policy', to: '/page/privacy-policy' },
    { label: 'Terms of Service', to: '/page/terms' },
    { label: 'Safety Tips', to: '/page/safety-tips' },
    { label: 'Contact Us', to: '/contact' },
    { label: 'About', to: '/about' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px 14px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {featuredAds.length === 0
          ? [1, 2, 3, 4].map((i) => (
              <div key={i} className="d-flex gap-2 p-2">
                <div className="skeleton" style={{ width: 56, height: 56, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton mb-1" style={{ height: 12, width: '80%', borderRadius: 4 }} />
                  <div className="skeleton" style={{ height: 12, width: '50%', borderRadius: 4 }} />
                </div>
              </div>
            ))
          : featuredAds.map((ad: any) => (
              <Link
                key={ad.id}
                to={`/ad/${ad.slug}`}
                className="d-flex gap-2 text-decoration-none p-2"
                style={{
                  transition: 'background 0.15s ease',
                  borderBottom: '1px solid var(--pw-border-soft)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--pw-bg-soft)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    overflow: 'hidden',
                    background: 'var(--pw-bg-soft)',
                  }}
                >
                  <img
                    src={getAbsoluteImageUrl(ad.primaryImage) || '/placeholder-ad.png'}
                    alt={ad.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--pw-text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.4,
                    }}
                  >
                    {ad.title}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--pw-primary-strong)', marginTop: 2 }}>
                    Rs. {formatPrice(ad.price)}
                  </div>
                  {ad.city && (
                    <div className="d-flex align-items-center gap-1" style={{ fontSize: 11, color: 'var(--pw-text-muted)', marginTop: 2 }}>
                      <MapPinIcon style={{ width: 10, height: 10, flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ad.city}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
      </div>

      {/* Sticky footer links */}
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 12,
          borderTop: '1px solid var(--pw-border)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
          {footerLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              style={{
                fontSize: 11,
                color: 'var(--pw-text-muted)',
                textDecoration: 'none',
                transition: 'color 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--pw-primary)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--pw-text-muted)')}
            >
              {link.label}
            </Link>
          ))}
        </div>
        <div style={{ fontSize: 10, color: 'var(--pw-text-muted)', marginTop: 8 }}>
          © {new Date().getFullYear()} NayaBajar
        </div>
      </div>
    </div>
  );
};

export default PromotionsSidebar;
